import rawQueryBulas, { QueryBulasRequest, DominioBean } from 'bula-certa-commons/service/bulas/query-bulas-reacoes-raw'

export { type QueryBulasRequest, type DominioBean }

export type QueryBulasResponse = {
    duration: number
    qtdeFabricates: number
    qtdeCoberturasCompletas: number
    qtdeCoberturaParciais: number
    qtdeNaoDocumentadas: number
    grade: GradeReacaoPorFabricanteData
}

export enum Frequencia {
    Comum = 0,
    PoucoComum = 1,
    Frequente = 2,
    Rara = 3
}

export enum Gravidade {
    Leve = 0,
    Moderada = 1,
    Grave = 2
}

export type ReacaoProdutoInfo = {
    frequencia: Frequencia
    gravidade: Gravidade
}

export type MedicamentoReativoInfo = {
    id: string
    nome: string
}

export type GradeReacaoPorFabricanteRow = {
    id: string
    reacao: string
    qtdeCasos: number
    medicamentos: MedicamentoReativoInfo[][]
}

export type FabricanteInfo = {
    id: string
    nome: string
}

export type GradeReacaoPorFabricanteData = {
    fabricantes: FabricanteInfo[]
    rows: GradeReacaoPorFabricanteRow[]
}

export default async (rq: QueryBulasRequest): Promise<QueryBulasResponse> => {
    const data = await rawQueryBulas(rq)

    // 1. Mapa de medicamentos por id (para acesso rápido)
    const medicamentoMap = new Map<string, DominioBean>()
    for (const med of data.dominios.medicamentos) {
        medicamentoMap.set(med.id, med)
    }

    // 2. Estrutura intermediária: Map<Reação + Fabricante, Set<medicamentoId>>
    // Usamos Set para evitar duplicatas de medicamentos
    const reacaoFabricanteMedicamentos = new Map<string, Set<string>>()

    // Chave composta simples como string: `${reacaoId}|${fabricanteId}`
    for (const [idFabricante, [idMedicamento, reacoesIds]] of data.relation) {
        for (const reacaoId of reacoesIds) {
            const key = `${reacaoId}|${idFabricante}`

            let idMedSet = reacaoFabricanteMedicamentos.get(key)
            if (!idMedSet) {
                idMedSet = new Set()
                reacaoFabricanteMedicamentos.set(key, idMedSet)
            }
            idMedSet.add(idMedicamento)
        }
    }

    // 3. Construção das linhas da grade (uma por reação)
    const rows: GradeReacaoPorFabricanteRow[] = []

    for (const reacao of data.dominios.reacoes) {
        const reacaoId = reacao.id
        const medicamentosPorFabricante: MedicamentoReativoInfo[][] = []

        // Para cada fabricante, coletamos os medicamentos relacionados a essa reação
        for (const fabricante of data.dominios.fabricantes) {
            const fabId = fabricante.id
            const key = `${reacaoId}|${fabId}`

            const idMedSet = reacaoFabricanteMedicamentos.get(key)
            if (!idMedSet || idMedSet.size === 0) {
                medicamentosPorFabricante.push([])
                continue
            }

            const medicamentos: MedicamentoReativoInfo[] = []

            for (const medId of idMedSet) {
                const medDto = medicamentoMap.get(medId)
                if (medDto) {
                    medicamentos.push({ id: medDto.id, nome: medDto.nome })
                }
            }

            medicamentosPorFabricante.push(medicamentos)
        }

        // No caso atual, qtdeCasos está sempre 0
        // Se futuramente vier do backend, pode-se calcular aqui
        rows.push({
            id: reacaoId,
            reacao: reacao.nome,
            qtdeCasos: 0,
            medicamentos: medicamentosPorFabricante
        })
    }

    // 4. Fabricantes
    const fabricantes: FabricanteInfo[] = data.dominios.fabricantes.map((f) => ({
        id: f.id,
        nome: f.nome
    }))

    // 5. Montagem da resposta final
    return {
        duration: data.duration,

        // totais (mantidos como no original)
        qtdeFabricates: data.totais.qtdeFabricates,
        qtdeCoberturasCompletas: data.totais.qtdeCoberturasCompletas,
        qtdeCoberturaParciais: data.totais.qtdeCoberturaParciais,
        qtdeNaoDocumentadas: data.totais.qtdeNaoDocumentadas,

        // nova estrutura desejada
        grade: {
            fabricantes,
            rows
        }
    }
}
